import React, { useEffect, useState } from "react";
import { Box, Drawer } from "@mui/material";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import ConsoleTopBar from "../components/console/ConsoleTopBar";
import JobsRail from "../components/console/JobsRail";
import ProfilePanel from "../pages/Console/ProfilePanel";
import CommandBar from "../components/console/CommandBar";
import {
  ConsoleProvider,
  useConsole,
  jobIdOf,
} from "../pages/Console/ConsoleContext";
import { UploadProvider } from "../../store/uploadContext";
import { getUserById } from "../../services/userService";
import tokens from "../theme/tokens";

const { color } = tokens;

/** Split out so it can call useConsole() — it must sit inside ConsoleProvider. */
const ConsoleCommandBar = () => {
  const { jobs, commandOpen, setCommandOpen, commands } = useConsole();
  return (
    <CommandBar
      open={commandOpen}
      onClose={() => setCommandOpen(false)}
      jobs={jobs}
      jobIdOf={jobIdOf}
      candidates={commands.candidates}
      actions={commands.actions}
    />
  );
};

/**
 * Shell for the job console: 48px top bar + 264px jobs rail + outlet.
 * Deliberately NOT DashboardLayout — the console replaces the drawer + JobsDock
 * navigation entirely. Legacy dashboards still render under DashboardLayout.
 */
const ConsoleLayout = () => {
  const userId = useSelector((state) => state.appreducer.account.userId);
  const [userDetails, setUserDetails] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    getUserById(userId)
      .then(setUserDetails)
      .catch((err) => console.error("Error fetching user details:", err));
  }, [userId]);

  const updateUserDetails = (updated) =>
    setUserDetails((prev) => ({ ...prev, ...updated }));

  return (
    // UploadProvider wraps the whole shell (not a single route as before), so upload
    // progress survives switching jobs or tabs while resumes are still processing.
    <UploadProvider>
    <ConsoleProvider>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: color.bg,
          overflow: "hidden",
        }}
      >
        <ConsoleTopBar user={userDetails} onOpenRail={() => setRailOpen(true)} />
        <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
          {/* Desktop: the rail is always present. Below 900px it becomes a sheet
              opened from the ☰ button in the top bar. */}
          <Box sx={{ display: { xs: "none", md: "flex" } }}>
            <JobsRail
              user={userDetails}
              userId={userId}
              onEditProfile={() => setEditOpen(true)}
            />
          </Box>
          <Drawer
            anchor="left"
            open={railOpen}
            onClose={() => setRailOpen(false)}
            sx={{ display: { xs: "block", md: "none" } }}
            PaperProps={{ sx: { width: "auto" } }}
          >
            <JobsRail
              user={userDetails}
              userId={userId}
              onEditProfile={() => setEditOpen(true)}
              onNavigate={() => setRailOpen(false)}
            />
          </Drawer>

          <Box component="main" sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Outlet />
          </Box>
        </Box>
      </Box>

      <ConsoleCommandBar />

      <ProfilePanel
        open={editOpen}
        onClose={() => setEditOpen(false)}
        user={userDetails}
        onSaved={updateUserDetails}
      />
    </ConsoleProvider>
    </UploadProvider>
  );
};

export default ConsoleLayout;
